/**
 * Replacement for antd's `theme.useToken()` — returns an antd-token-shaped object
 * (the fields the app actually reads: chart theming + a few inline styles), derived
 * from the app's own light/dark tokens. Lets us drop `import { theme } from 'antd'`
 * and keep ECharts/inline-styled bits theme-aware without the antd ConfigProvider.
 *
 * Migration: `import { theme } from '../../ui'` then `const { token } = theme.useToken()`.
 */
import { useThemeStore } from '../store/theme';
import { PALETTE } from '../lib/palette';

export interface ThemeToken {
  colorPrimary: string; colorSuccess: string; colorWarning: string; colorError: string; colorInfo: string;
  colorText: string; colorTextSecondary: string; colorTextTertiary: string; colorTextQuaternary: string;
  colorBorder: string; colorBorderSecondary: string;
  colorBgContainer: string; colorBgElevated: string; colorBgLayout: string;
  colorFill: string; colorFillSecondary: string; colorFillTertiary: string; colorFillQuaternary: string;
  colorPrimaryBg: string; colorPrimaryBorder: string; colorPrimaryText: string;
  colorSuccessBg: string; colorErrorBg: string; colorWarningBg: string; colorInfoBg: string;
  colorSuccessText: string; colorSuccessBorder: string; colorInfoText: string; colorInfoBorder: string;
  colorWarningText: string; colorWarningBorder: string; colorErrorText: string; colorErrorBorder: string;
  colorTextDisabled: string; colorSplit: string; colorFillContent: string;
  borderRadius: number;
}

const LIGHT: ThemeToken = {
  colorPrimary: PALETTE.brand[600], colorSuccess: PALETTE.success[500], colorWarning: PALETTE.harvest[400], colorError: PALETTE.danger[500], colorInfo: PALETTE.sky[500],
  colorText: '#18241d', colorTextSecondary: '#5d6a61', colorTextTertiary: '#889389', colorTextQuaternary: '#a9b3aa',
  colorBorder: '#ccd4c7', colorBorderSecondary: '#e1e7dd',
  colorBgContainer: '#ffffff', colorBgElevated: '#ffffff', colorBgLayout: '#edf0ea',
  colorFill: '#eaeee5', colorFillSecondary: '#f4f6f2', colorFillTertiary: '#f4f6f2', colorFillQuaternary: '#f4f6f2',
  colorPrimaryBg: '#edf8e7', colorPrimaryBorder: '#ace197', colorPrimaryText: PALETTE.brand[700],
  colorSuccessBg: '#edf8e7', colorErrorBg: '#fef2f2', colorWarningBg: '#fdf6e3', colorInfoBg: '#e6f2ff',
  colorSuccessText: '#237804', colorSuccessBorder: '#b7eb8f', colorInfoText: '#0958d9', colorInfoBorder: '#91caff',
  colorWarningText: '#ad6800', colorWarningBorder: '#ffe58f', colorErrorText: '#a8071a', colorErrorBorder: '#ffccc7',
  colorTextDisabled: '#b7c0b8', colorSplit: 'rgba(225,231,221,0.6)', colorFillContent: '#eaeee5',
  borderRadius: 6,
};

const DARK: ThemeToken = {
  colorPrimary: PALETTE.brand[600], colorSuccess: PALETTE.success[500], colorWarning: PALETTE.harvest[400], colorError: PALETTE.danger[500], colorInfo: PALETTE.sky[400],
  colorText: '#e8ebe8', colorTextSecondary: '#9ba49c', colorTextTertiary: '#6b746d', colorTextQuaternary: '#565e57',
  colorBorder: '#3f473f', colorBorderSecondary: '#2e362f',
  colorBgContainer: '#1f2621', colorBgElevated: '#28302a', colorBgLayout: '#161a17',
  colorFill: '#323a34', colorFillSecondary: '#28302a', colorFillTertiary: '#28302a', colorFillQuaternary: '#28302a',
  colorPrimaryBg: 'rgba(51,144,34,0.16)', colorPrimaryBorder: 'rgba(51,144,34,0.42)', colorPrimaryText: PALETTE.brand[400],
  colorSuccessBg: 'rgba(51,144,34,0.16)', colorErrorBg: 'rgba(217,54,62,0.16)', colorWarningBg: 'rgba(231,178,44,0.16)', colorInfoBg: 'rgba(69,150,255,0.16)',
  colorSuccessText: '#73d13d', colorSuccessBorder: 'rgba(82,196,26,0.4)', colorInfoText: '#69b1ff', colorInfoBorder: 'rgba(69,150,255,0.4)',
  colorWarningText: '#ffd666', colorWarningBorder: 'rgba(231,178,44,0.4)', colorErrorText: '#ff7875', colorErrorBorder: 'rgba(217,54,62,0.4)',
  colorTextDisabled: '#565e57', colorSplit: 'rgba(46,54,47,0.6)', colorFillContent: '#323a34',
  borderRadius: 6,
};

/** Hook returning `{ token }` like antd's `theme.useToken()`; re-renders on theme change. */
export function useToken(): { token: ThemeToken } {
  // Subscribe to `mode` so the token updates when the user flips the theme.
  useThemeStore((s) => s.mode);
  const resolved = useThemeStore((s) => s.resolved)();
  return { token: resolved === 'dark' ? DARK : LIGHT };
}

/** antd-compatible namespace: `theme.useToken()`. */
export const theme = { useToken };
